import { useMemo } from 'react'
import { differenceInCalendarDays } from 'date-fns'
import { Subtitle, Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { Head, useQuery } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/PageBreadcrumbs'
import { TabContent } from './sections/TabContent'
import { RightColumn } from './sections/RightColumn'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import { DataCard } from './sections/DataCard'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import useTransform from '@app/util/useTransform'
import { ViewPlotModel } from 'functions/app/plot/view'
import { isUuid } from '@app/util/helpers'

export type PlotPageProps = {
  plotId: string
}

export const PlotPage = ({ plotId }: PlotPageProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app
  const transformStyle = useTransform()

  const queryPayload = useMemo(() => {
    return isUuid(plotId) ? { id: plotId } : { slug: plotId }
  }, [plotId])

  const plotQuery = useQuery('view-plot', queryPayload)
  const plot: ViewPlotModel | null = plotQuery.data

  const daysLeft = useMemo(() => {
    if (!plot?.applicationDeadline) {
      return null
    }
    return differenceInCalendarDays(plot.applicationDeadline, Date.now())
  }, [plot?.applicationDeadline])

  const offeredBy = useMemo(() => {
    if (!plot?.owner?.name) {
      return null
    }
    return `${translations.plotPage.offeredBy} ${plot.owner.name}`
  }, [translations.plotPage.offeredBy, plot?.owner?.name])

  if (!plot) {
    return (
      <PageLayout gap="2rem">
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '90vh' }}
        >
          <PageBreadcrumbs
            level2={translations.pageNames.plots}
            level3="Loading..."
          />
        </Column>
      </PageLayout>
    )
  }

  if (isMobile) {
    return (
      <PageLayout gap="0rem" standardPageTitle={false}>
        <Head>
          <title>{`${plot.name} | CrowdBuilding`}</title>
          <meta name="description" content={plot.description} />
          {plot.media?.length > 0 && (
            <meta property="og:image" content={plot.media[0].src} />
          )}
        </Head>
        <Column alignItems="stretch" gap="1.5rem">
          <Column>
            <Column gap="0.5rem">
              <Title.H3>{plot.name}</Title.H3>
              {offeredBy && <Title.H5>{offeredBy}</Title.H5>}
            </Column>
            {plot.byCrowdbuilding && daysLeft && daysLeft > 0 && (
              <Text.Medium style={{ color: 'var(--palette-red-400)' }}>
                {translations.plotPage.applicationClosesInLabel.replace(
                  '{0}',
                  daysLeft.toString(),
                )}
              </Text.Medium>
            )}
            <Text.Medium>{plot.description}</Text.Medium>
          </Column>
          <PageMediaGallery title={plot.name} images={plot.media} />
          <DataCard plot={plot} />
        </Column>
        <TabContent plot={plot} />
      </PageLayout>
    )
  }

  return (
    <PageLayout gap="2rem" standardPageTitle={false}>
      <Head>
        <title>{`${plot.name} | CrowdBuilding`}</title>
        <meta name="description" content={plot.description} />
      </Head>
      <Column gap="1.5rem" alignItems="stretch" style={transformStyle}>
        <PageBreadcrumbs
          level2={translations.pageNames.plots}
          level3={plot.name}
        />
        <Title.H1 style={{ paddingRight: '24rem' }}>{plot.name}</Title.H1>
        <Column style={{ paddingRight: '24rem' }}>
          {plot.byCrowdbuilding && daysLeft && daysLeft > 0 && (
            <Text.Large style={{ color: 'var(--palette-red-400)' }}>
              {translations.plotPage.applicationClosesInLabel.replace(
                '{0}',
                daysLeft.toString(),
              )}
            </Text.Large>
          )}
          <Subtitle>{plot.description}</Subtitle>
        </Column>
        <Row
          gap="1.5rem"
          style={{
            alignItems: 'flex-start',
            ...transformStyle,
          }}
        >
          <Column alignItems="stretch" gap="2rem" style={{ flex: '2 1 62rem' }}>
            <PageMediaGallery
              title={plot.name}
              images={plot.media}
              defaultImage={defaultProjectPicture}
              style={{ width: '100%' }}
            />
            <TabContent plot={plot} />
          </Column>
          <RightColumn plot={plot} />
        </Row>
      </Column>
    </PageLayout>
  )
}
